import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/session/auth/auth.utils';
import {
    AUTH_IMPERSONATION_SELECTION_KEYS,
    AUTH_REFRESH_FAILURE_RESET_KEYS,
    AUTH_SESSION_RESET_KEYS,
    AUTH_STORAGE_KEYS,
    AuthStorage,
    AuthStorageKey
} from 'app/session/auth/auth.storage';
import { ProfileService } from 'app/modules/profile/profile.service';
import { FuseConfigService } from '@fuse/services/config';
import { environment } from 'environments/environment';
import { apiMessageOptions } from 'app/shared/interceptors/api-message-context';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    private _unauthorizedHandled: boolean = false;
    private readonly _tokenRefreshIntervalMs: number = 15 * 60 * 1000;
    private readonly _tokenTimestampKey: AuthStorageKey = AUTH_STORAGE_KEYS.accessTokenTimestamp;
    private _refreshInFlight$: Observable<boolean> | null = null;
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: ProfileService,
        private _fuseConfigService: FuseConfigService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        AuthStorage.set(AUTH_STORAGE_KEYS.accessToken, token);
        AuthStorage.set(this._tokenTimestampKey, `${Date.now()}`);
    }

    get accessToken(): string {
        return AuthStorage.get(AUTH_STORAGE_KEYS.accessToken);
    }

    isTokenRefreshDue(): boolean {
        if (!this.accessToken) {
            return false;
        }

        const storedTimestamp = Number(AuthStorage.get(this._tokenTimestampKey) || 0);

        // Missing or invalid timestamp means token should be refreshed on next action.
        if (!Number.isFinite(storedTimestamp) || storedTimestamp <= 0) {
            return true;
        }

        return (Date.now() - storedTimestamp) >= this._tokenRefreshIntervalMs;
    }

    refreshTokenIfDue(): Observable<boolean> {
        if (!this.accessToken) {
            return of(false);
        }

        if (!this.isTokenRefreshDue()) {
            return of(true);
        }

        if (this._refreshInFlight$) {
            return this._refreshInFlight$;
        }

        this._refreshInFlight$ = this.signInUsingToken().pipe(
            finalize(() => {
                this._refreshInFlight$ = null;
            }),
            shareReplay(1)
        );

        return this._refreshInFlight$;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string, password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            console.error('[AuthService] Sign-in blocked: user is already marked as authenticated.');
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient.post(
            `${environment.api_url}/api/session/sign-in`,
            credentials,
            apiMessageOptions(false)
        )
            .pipe(
                switchMap((response: any) => {
                    const payload = this._applySessionResponse(response, true);

                    if (!payload?.token || !payload?.user || !Array.isArray(payload?.organizations)) {
                        console.error('[AuthService] Sign-in response is missing expected fields.', {
                            hasToken: !!payload?.token,
                            hasUser: !!payload?.user,
                            hasOrganizations: Array.isArray(payload?.organizations),
                            response
                        });
                    }

                    return of(payload || response);
                }),
                catchError((error) => {
                    console.error('[AuthService] Sign-in request failed.', {
                        endpoint: `${environment.api_url}/api/session/sign-in`,
                        error
                    });
                    return throwError(() => error);
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post(
            `${environment.api_url}/api/session/refresh-token`,
            {},
            apiMessageOptions(false)
        ).pipe(
            catchError((error) => {
                console.error('[AuthService] Token refresh failed.', {
                    endpoint: `${environment.api_url}/api/session/refresh-token`,
                    error
                });

                AuthStorage.removeMany(AUTH_REFRESH_FAILURE_RESET_KEYS);
                this._authenticated = false;

                // Return false
                return of(false);
            }),
            switchMap((response: any) => {
                const payload = this._extractPayload(response);

                if (!payload || typeof payload !== 'object' || !payload.token) {
                    console.error('[AuthService] Token refresh returned an invalid payload.', {
                        response
                    });
                    return of(false);
                }

                this._applySessionResponse(response, true);

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Forgot password
     *
     * @param user
     */
    forgotPassword(user: { email: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/forget-password`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Activate account
     *
     * @param user
     */
    activateAccount(user: { email: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/activate-account`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Validate OTP
     *
     * @param user
     */
    validateOTP(user: { email: string, code: string, reference: string, action: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/dashboard/validate-otp`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Reset password
     *
     * @param user  
     */
    resetPassword(user: { email: string, reference: string, code: string, password_new: string, password_confirm: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/reset-password`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/sign-out`,
            {},
            apiMessageOptions(false)
        ).pipe(
            catchError(() => {

                // Continue logout flow even if API sign-out fails
                return of(true);
            }),
            switchMap(() => {

                // Remove session data from local storage
                AuthStorage.removeMany(AUTH_SESSION_RESET_KEYS);
                this._userService.selectedOrganization = null;
                this._userService.selectedBranch = null;

                // Set the authenticated flag to false
                this._authenticated = false;

                // Return the observable
                return of(true);
            })
        );
    }

    impersonate(email: string): Observable<any> {
        const wasAlreadyImpersonating = this.isImpersonating();
        const targetEmail = `${email || ''}`.trim();

        if (!targetEmail) {
            return throwError(() => new Error('Target user email is required for impersonation.'));
        }

        if (!AuthStorage.get(AUTH_STORAGE_KEYS.impersonatorAccessToken) && this.accessToken) {
            AuthStorage.set(AUTH_STORAGE_KEYS.impersonatorAccessToken, this.accessToken);
        }

        return this._httpClient.post(
            `${environment.api_url}/api/session/impersonate`,
            { email: targetEmail },
            apiMessageOptions(false)
        ).pipe(
            switchMap((response: any) => {
                // Save current selections only on the first impersonation
                if (!wasAlreadyImpersonating) {
                    const currentOrg = AuthStorage.get(AUTH_STORAGE_KEYS.selectedOrganization);
                    const currentBranch = AuthStorage.get(AUTH_STORAGE_KEYS.selectedBranch);
                    if (currentOrg) {
                        AuthStorage.set(AUTH_STORAGE_KEYS.impersonatorSelectedOrganization, currentOrg);
                    } else {
                        AuthStorage.remove(AUTH_STORAGE_KEYS.impersonatorSelectedOrganization);
                    }
                    if (currentBranch) {
                        AuthStorage.set(AUTH_STORAGE_KEYS.impersonatorSelectedBranch, currentBranch);
                    } else {
                        AuthStorage.remove(AUTH_STORAGE_KEYS.impersonatorSelectedBranch);
                    }
                }

                // Skip immediate user/org broadcasts here and let the dashboard bootstrapping reload clean state.
                const payload = this._applySessionResponse(response, false, false);
                return of(payload || response);
            }),
            catchError((error) => {
                if (!this.isImpersonating()) {
                    AuthStorage.remove(AUTH_STORAGE_KEYS.impersonatorAccessToken);
                }
                return throwError(() => error);
            })
        );
    }

    unimpersonate(): Observable<boolean> {
        const originalToken = AuthStorage.get(AUTH_STORAGE_KEYS.impersonatorAccessToken);
        if (!originalToken) {
            return of(false);
        }

        this.accessToken = originalToken;

        return this.signInUsingToken().pipe(
            map((ok) => {
                if (ok) {
                    AuthStorage.remove(AUTH_STORAGE_KEYS.impersonatorAccessToken);

                    // Restore original selections
                    const savedOrg = AuthStorage.get(AUTH_STORAGE_KEYS.impersonatorSelectedOrganization);
                    const savedBranch = AuthStorage.get(AUTH_STORAGE_KEYS.impersonatorSelectedBranch);
                    try {
                        this._userService.selectedOrganization = savedOrg ? JSON.parse(savedOrg) : null;
                    } catch (e) {
                        this._userService.selectedOrganization = null;
                    }
                    try {
                        this._userService.selectedBranch = savedBranch ? JSON.parse(savedBranch) : null;
                    } catch (e) {
                        this._userService.selectedBranch = null;
                    }
                    AuthStorage.removeMany(AUTH_IMPERSONATION_SELECTION_KEYS);

                    return true;
                }
                return false;
            }),
            catchError(() => of(false))
        );
    }

    isImpersonating(): boolean {
        return !!AuthStorage.get(AUTH_STORAGE_KEYS.impersonatorAccessToken);
    }

    getStoredUser(): any | null {
        return AuthStorage.getJson<any>(AUTH_STORAGE_KEYS.user);
    }

    isWorkspaceSelectionRequired(): boolean {
        if (!this.accessToken) {
            return false;
        }

        const user = this.getStoredUser();
        const isSuperAdmin = AuthUtils.isSuperAdminUser(user)
            || AuthUtils.hasRole(user?.role, ['@SUPERADMIN'], user);

        return !isSuperAdmin;
    }

    hasWorkspaceSelection(): boolean {
        const organization = AuthStorage.getJson<any>(AUTH_STORAGE_KEYS.selectedOrganization);
        const branch = AuthStorage.getJson<any>(AUTH_STORAGE_KEYS.selectedBranch);

        return !!organization?.code && !!branch?.code;
    }

    hasWorkspaceAccess(): boolean {
        if (!this.isWorkspaceSelectionRequired()) {
            return true;
        }

        return this.hasWorkspaceSelection();
    }

    private _restoreSavedWorkspaceSelection(organizations: any[], user: any = null): void {
        const savedOrganization = this._parseStoredSelection(AUTH_STORAGE_KEYS.selectedOrganization);
        const savedBranch = this._parseStoredSelection(AUTH_STORAGE_KEYS.selectedBranch);

        const matchedOrganization = savedOrganization?.code
            ? organizations.find((organization: any) => organization?.code === savedOrganization.code) || null
            : null;

        const matchedBranch = matchedOrganization && savedBranch?.code
            ? (matchedOrganization?.branches || []).find((branch: any) => branch?.code === savedBranch.code) || null
            : null;

        if (matchedOrganization && matchedBranch) {
            this._userService.selectedOrganization = matchedOrganization;
            this._userService.selectedBranch = matchedBranch;

            const normalizedUser = this._normalizeUser(user, organizations, matchedOrganization, matchedBranch);
            if (normalizedUser) {
                this._userService.user = normalizedUser;
            }

            if (matchedOrganization?.config) {
                this._fuseConfigService.applyPermanentLayoutChanges(matchedOrganization.config);
            }
            return;
        }

        this._fuseConfigService.clearTemporaryLayoutChanges();
        this._userService.selectedOrganization = null;
        this._userService.selectedBranch = null;
    }

    private _applySessionResponse(response: any, preserveSavedSelection: boolean, emitState: boolean = true): any {
        const payload = this._extractPayload(response);
        // Stocke le dernier payload reçu pour le debugger
        const token = `${payload?.token || ''}`;
        const organizations = this._normalizeOrganizations(payload?.organizations ?? payload?.organization);
        const access = payload?.access ?? null;
        const normalizedUser = this._normalizeUser({
            ...(payload?.user || {}),
            superadmin: payload?.superadmin === true || payload?.user?.superadmin === true,
            extra: {
                ...((payload?.user as any)?.extra || {}),
                workspace_required: payload?.workspace_required === true
            }
        }, organizations);

        if (token) {
            this.accessToken = token;
            this._authenticated = true;
        }

        AuthStorage.setJson(AUTH_STORAGE_KEYS.organizations, organizations || []);

        if (access !== null && access !== undefined) {
            AuthStorage.setJson(AUTH_STORAGE_KEYS.access, access);
        } else {
            AuthStorage.remove(AUTH_STORAGE_KEYS.access);
        }

        if (normalizedUser) {
            AuthStorage.setJson(AUTH_STORAGE_KEYS.user, normalizedUser);
        }

        if (emitState && normalizedUser) {
            this._userService.user = normalizedUser;
        }

        if (emitState) {
            this._userService.organizations = organizations;
        }

        if (emitState && preserveSavedSelection) {
            this._restoreSavedWorkspaceSelection(organizations, normalizedUser);
        } else {
            this._fuseConfigService.clearTemporaryLayoutChanges();
            this._userService.selectedOrganization = null;
            this._userService.selectedBranch = null;
        }

        return {
            ...payload,
            user: normalizedUser,
            organizations,
            access,
            token
        };
    }

    private _extractPayload(response: any): any {
        return response?.payload && typeof response.payload === 'object'
            ? response.payload
            : response;
    }

    private _normalizeOrganizations(organizations: any): any[] {
        if (Array.isArray(organizations)) {
            return organizations.map((organization: any) => this._normalizeOrganization(organization));
        }

        if (organizations && typeof organizations === 'object') {
            return Object.values(organizations).map((organization: any) => this._normalizeOrganization(organization));
        }

        return [];
    }

    private _normalizeOrganization(organization: any): any {
        const branches = Array.isArray(organization?.branches)
            ? organization.branches
            : organization?.branches && typeof organization.branches === 'object'
                ? Object.values(organization.branches)
                : [];

        return {
            ...organization,
            branches
        };
    }

    private _normalizeUser(user: any, organizations: any[], selectedOrganization: any = null, selectedBranch: any = null): any {
        if (!user) {
            return null;
        }

        const roleMap = this._extractRoleMap(user?.role) || this._extractRoleMap(user?.extra?.role_map);
        const resolvedRoles = this._resolveEffectiveRoles(roleMap || user?.role, organizations, selectedOrganization, selectedBranch, user);

        return {
            ...user,
            role: resolvedRoles,
            extra: {
                ...(user?.extra || {}),
                role_map: roleMap || user?.extra?.role_map || null
            }
        };
    }

    private _extractRoleMap(value: any): Record<string, any> | null {
        return value && typeof value === 'object' && !Array.isArray(value)
            ? value as Record<string, any>
            : null;
    }

    private _resolveEffectiveRoles(roleSource: any, organizations: any[] = [], selectedOrganization: any = null, selectedBranch: any = null, user: any = null): string[] {
        const roleMap = this._extractRoleMap(roleSource);
        const fallbackOrganizationCode = selectedOrganization?.code
            || organizations?.[0]?.code
            || Object.keys(roleMap || {})[0]
            || null;

        return AuthUtils.resolveEffectiveRoles(roleSource, {
            selectedOrganizationCode: selectedOrganization?.code || null,
            selectedBranchCode: selectedBranch?.code || null,
            fallbackOrganizationCode,
            user
        });
    }

    private _parseStoredSelection(storageKey: AuthStorageKey): any | null {
        return AuthStorage.getJson<any>(storageKey);
    }

    // handleUnauthorized(error: any, requestUrl: string): void {
    //     if (this._unauthorizedHandled) {
    //         return;
    //     }

    //     this._unauthorizedHandled = true;

    //     console.error('[AuthService] Unauthorized response detected. Clearing session and redirecting to sign-in.', {
    //         requestUrl,
    //         error
    //     });

    //     localStorage.removeItem('access_token');
    //     localStorage.removeItem('impersonator_access_token');
    //     this._authenticated = false;

    //     setTimeout(() => {
    //         this._unauthorizedHandled = false;
    //     }, 3000);

    //     window.location.assign('/sign-in');
    // }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string, email: string, password: string, company: string }): Observable<any> {
        return this._httpClient.post(
            'api/auth/sign-up',
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string, password: string }): Observable<any> {
        return this._httpClient.post(
            'api/auth/unlock-session',
            credentials,
            apiMessageOptions(false)
        );
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        if (!this.isTokenRefreshDue()) {
            this._authenticated = true;
            return of(true);
        }

        // Refresh token only when the configured 15-minute window has elapsed.
        return this.refreshTokenIfDue();
    }
}
